import { OnInit, Component, Input, ViewChild } from '@angular/core';
import { LoaderService } from 'src/modules/base/services/loader.service';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '../../../dialog/dialog.service';
import { AlertsService } from 'src/modules/base/services/alerts.service';
import { DashboardService } from '../../services/dashboard.service';
import { LazyService } from '../../../../services/lazy.service';
import { FilterState } from '../dashboard-filters/dashboard-filters.component'; // Import the interface
import { UtilService } from 'src/modules/base/services/util.service';

@Component({
    selector: 'dashboard-powerbi',
    templateUrl: './powerbi.component.html',
    styleUrls: ['./powerbi.component.scss'],
    standalone: false
})
export class PowerBIComponent implements OnInit {

  @Input() data: any;

  config: any = {data:{}};
  isEmbedLoading: boolean = false;
  reportRef: any; 
  
  // Store current filter state
  currentFilters: FilterState;

  fySettings = {
    startMonth: 3, // 0 = Jan, 3 = April
    isYearAhead: false 
  };

  constructor(
    private lazyService: LazyService,
    private loader: LoaderService,
    public router: Router,
    public route: ActivatedRoute,
    public dialogService: DialogService,
    public alerts: AlertsService,
    public utilService: UtilService,
    public dashBoardService: DashboardService
  ) {}

  ngOnInit() {
    // We just load the script here. 
    // We DO NOT call build() yet. We wait for the Filter Component to emit the initial state.
    if((window as any).powerbi == undefined){
      this.lazyService.loadScript("/assets/js/powerbi.min.js");
    }
  }

  /**
   * This is the MAIN entry point now. 
   * It is called by the Child Component when it loads, and whenever the user changes a filter.
   */
  handleFilterUpdate(filterState: FilterState) {
    this.currentFilters = filterState;

    // If report is already running, just update filters
    if (this.reportRef) {
      if(filterState.facilityChange){
        this.build();
      }else{
        this.updateReportFilters();  
      }
      
    } else {
      // First time load
      this.build();
    }
  }

  build() {
    try{
          this.loader.showSpinner();
          this.isEmbedLoading = true; 

          // Check if facility ID is present to pass to API
          let configObject: any = {};
          if(this.currentFilters && this.currentFilters.facilityId) {
              configObject.facility_id = this.currentFilters.facilityId;
          }

          this.dashBoardService.getWidgetData(this.data.widget_id, (widget) => {
            this.loader.hideSpinner(); 
            if(widget){
              this.config.data = widget;
            this.config.data.token = this.route.snapshot.queryParams.token;
            
            configObject.url = btoa(this.config.data.link);
            configObject.report_id = widget.query_id;
            

            this.dashBoardService.getDashboardUrl(widget.id, 'powerbi', configObject, (response) => {
              if(!response){
                this.isEmbedLoading = false;
              }
              try{
                var tokenResponse = JSON.parse(response)
                if (tokenResponse) {
                  this.config.data.token = tokenResponse.embedToken;
                  this.config.data.embedUrl = tokenResponse.embedUrl;
                  this.config.data.reportId = tokenResponse.reportId;
                  setTimeout(()=>{
                    this.embedPowerBI();
                  }, 500)
                } else {
                  this.alerts.error("POWER BI CONFIG ERROR");
                  this.isEmbedLoading = false;
                }
              } catch(e){
                console.error(e);
                this.alerts.error("POWER BI CONFIG ERROR");
                this.isEmbedLoading = false;
              }
            });
            }else{
              this.isEmbedLoading = false;
            }
            
          });
    }catch(e){
      console.error(e);
      this.alerts.error("POWER BI CONFIG ERROR");
      this.isEmbedLoading = false;
    }
  }

 // Add these properties to your class (or reset them before calling embedPowerBI)
retryCount: number = 0;
maxRetries: number = 2; // 2 retries is usually enough

embedPowerBI() {
    const powerbi = (window as any).powerbi;
    if (!powerbi) { return; }
    const models = (window as any)['powerbi-client'].models;

    // Construct filters using current state
    const initialFilters = this.getConstructedFilters(models);

    const embedConfig: any = {
        type: "report",
        id: this.config.data.reportId,
        embedUrl: this.config.data.embedUrl,
        accessToken: this.config.data.token,
        tokenType: models.TokenType.Embed,
        filters: initialFilters,
        settings: {
            panes: {
                filters: { visible: false },
                pageNavigation: { visible: true }
            },
            background: models.BackgroundType.Transparent
        }
    };

    if(this.utilService.isMobile()){
      embedConfig["settings"]["layoutType"] = models.LayoutType.MobilePortrait
    }

    const container = document.getElementById("powerbi-container");
    
    // IMPORTANT: resetting the container is crucial before re-embedding
    powerbi.reset(container); 
    
    this.reportRef = powerbi.embed(container, embedConfig);

    this.reportRef.on("rendered", () => {
        // Reset retry count on success so future reloads work correctly
        this.retryCount = 0; 
        setTimeout(() => { 
          this.isEmbedLoading = false; 
          const iframe = document
              .getElementById('powerbi-container')
              ?.querySelector('iframe');
          
            if (iframe) {
              iframe.style.height = this.utilService.isMobile()
                ?( window.innerHeight-50) + 'px'
                : '100%';
          
              iframe.style.width = '100%';
              iframe.style.border = 'none';

            }
        }, 0);
        
    });

    this.reportRef.on("error", (event: any) => {
        const errorDetail = event.detail;
        console.error("Power BI Embed Error:", errorDetail);

        // Check if we have retries left
        if (this.retryCount < this.maxRetries) {
            this.retryCount++;
            console.warn(`Model might be cold. Retrying attempt ${this.retryCount} in 6 seconds...`);

            // Wait 6 seconds to allow the backend capacity to finish mounting the dataset
            setTimeout(() => {
                this.embedPowerBI();
            }, 6000);
        } else {
            // If max retries reached, stop loading and show error
            this.isEmbedLoading = false;
            console.error("Max retries reached. Model could not load.");
        }
    });
}

  updateReportFilters() {
    if (!this.reportRef) return;
    const models = (window as any)['powerbi-client'].models;
    const newFilters = this.getConstructedFilters(models);
    
    this.reportRef.updateFilters(models.FiltersOperations.Replace, newFilters)
      .catch(errors => console.error("Error updating filters", errors));
  }

  

  getConstructedFilters(models: any) {
    const filters = [];
    if (this.currentFilters && this.currentFilters.fy && this.currentFilters.timezone && this.config.data != undefined && this.config.data.timeFilterTargets != undefined && Array.isArray(this.config.data.timeFilterTargets) && this.config.data.timeFilterTargets.length>0) {
      
      // Use data from the Child Component
      const { startMs, endMs } = this.dashBoardService.getEpochRange(this.fySettings,this.currentFilters.fy, this.currentFilters.timezoneOffset);
      
      this.config.data.timeFilterTargets.forEach(target => {
        const rangeFilter = {
          $schema: "http://powerbi.com/product/schema#advanced",
          target: { table: target.table, column: target.column },
          logicalOperator: "And",
          conditions: [
            { operator: "GreaterThanOrEqual", value: startMs },
            { operator: "LessThanOrEqual", value: endMs }
          ]
        };
        filters.push(rangeFilter);
      });
    }
    return filters;
  }
}